import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useBranding } from "../../hooks/useBranding";
import { useMenuSettings } from "../../hooks/useMenuSettings";
import { MENU, canShow } from "../../config/modules";
import { ProfileModal } from "../common/ProfileModal";
import { ChevronDown, ChevronRight, Server, X, LogOut } from "lucide-react";

const COLORS = {
  cyan: { icon: "text-cyan-400", activeSingle: "bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 font-semibold shadow-inner", activeGroup: "text-cyan-400 bg-cyan-500/10 font-semibold", child: "bg-cyan-500/15 text-cyan-300 font-semibold" },
  blue: { icon: "text-blue-400", activeSingle: "bg-blue-500/10 text-blue-400 border border-blue-500/30 font-semibold shadow-inner", activeGroup: "text-blue-400 bg-blue-500/10 font-semibold", child: "bg-cyan-500/15 text-cyan-300 font-semibold" },
  emerald: { icon: "text-emerald-400", activeSingle: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 font-semibold shadow-inner", activeGroup: "text-emerald-400 bg-emerald-500/10 font-semibold", child: "bg-emerald-500/15 text-emerald-300 font-semibold" },
  amber: { icon: "text-amber-400", activeSingle: "bg-amber-500/10 text-amber-400 border border-amber-500/30 font-semibold shadow-inner", activeGroup: "text-amber-400 bg-amber-500/10 font-semibold", child: "bg-amber-500/15 text-amber-300 font-semibold" },
  purple: { icon: "text-purple-400", activeSingle: "bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 font-semibold shadow-inner", activeGroup: "text-purple-400 bg-purple-500/10 font-semibold", child: "bg-purple-500/15 text-purple-300 font-semibold" },
  indigo: { icon: "text-indigo-400", activeSingle: "bg-indigo-500/10 text-indigo-400 border border-indigo-500/30 font-semibold shadow-inner", activeGroup: "text-indigo-400 bg-indigo-500/10 font-semibold", child: "bg-indigo-500/15 text-indigo-300 font-semibold" },
  teal: { icon: "text-teal-400", activeSingle: "bg-teal-500/10 text-teal-400 border border-teal-500/30 font-semibold shadow-inner", activeGroup: "text-teal-400 bg-teal-500/10 font-semibold", child: "bg-teal-500/15 text-teal-300 font-semibold" },
};

export const Sidebar = ({ isMobileOpen, setIsMobileOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const branding = useBranding();
  const { module_visibility, role_access } = useMenuSettings();

  const role = user?.role || "teknisi";
  const ctx = { visibility: module_visibility, role, roleAccess: role_access };

  const [showProfile, setShowProfile] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => {
    const st = {};
    MENU.forEach((m) => { if (m.base) st[m.key] = location.pathname.startsWith(m.base); });
    return st;
  });
  const toggleGroup = (key) => setOpenGroups((s) => ({ ...s, [key]: !s[key] }));

  const isActive = (path) => location.pathname === path;
  const closeMobile = () => { if (setIsMobileOpen) setIsMobileOpen(false); };
  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <>
      {isMobileOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden" onClick={closeMobile} />
      )}

      <aside
        data-testid="sidebar"
        className={`fixed top-0 bottom-0 left-0 z-50 w-64 bg-[#0d1527] border-r border-slate-800 flex flex-col transition-transform duration-300 ease-in-out lg:translate-x-0 ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        {/* Brand Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800/80 bg-[#090f1d]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-tr from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/20 overflow-hidden">
              {branding.app_logo ? (
                <img src={branding.app_logo} alt="Logo" className="w-full h-full object-contain" data-testid="sidebar-app-logo" />
              ) : (
                <Server className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <h1 className="font-bold text-base tracking-wide text-white leading-tight truncate max-w-[150px]" data-testid="sidebar-app-name">
                {branding.app_name || "NUSA NET"}
              </h1>
              <span className="text-[10px] text-slate-400 tracking-wider font-semibold uppercase">ISP Billing &amp; Ops</span>
            </div>
          </div>
          <button onClick={closeMobile} className="p-1 rounded-md text-slate-400 hover:text-white lg:hidden"><X className="w-5 h-5" /></button>
        </div>

        {/* Navigation Links */}
        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-1 text-sm font-medium">
          {MENU.map((m) => {
            if (!canShow(m.key, ctx)) return null;
            const c = COLORS[m.color] || COLORS.cyan;
            const Icon = m.icon;

            // Single item
            if (!m.children) {
              return (
                <Link
                  key={m.key}
                  to={m.path}
                  onClick={closeMobile}
                  data-testid={m.testid}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${isActive(m.path) ? c.activeSingle : "text-slate-300 hover:bg-slate-800/60 hover:text-white"}`}
                >
                  <Icon className={`w-4 h-4 ${c.icon}`} />
                  <span>{m.label}</span>
                </Link>
              );
            }

            // Group with children
            const visibleChildren = m.children.filter((ch) => canShow(ch.key, ctx));
            if (visibleChildren.length === 0) return null;
            const open = openGroups[m.key];
            return (
              <div key={m.key}>
                <button
                  onClick={() => toggleGroup(m.key)}
                  data-testid={m.testid}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all ${location.pathname.startsWith(m.base) ? c.activeGroup : "text-slate-300 hover:bg-slate-800/60 hover:text-white"}`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className={`w-4 h-4 ${c.icon}`} />
                    <span>{m.label}</span>
                  </div>
                  {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                </button>
                {open && (
                  <div className="pl-6 pr-1 py-1 space-y-1 mt-1 border-l-2 border-slate-800 ml-4">
                    {visibleChildren.map((ch) => {
                      const ChIcon = ch.icon;
                      return (
                        <Link
                          key={ch.key}
                          to={ch.path}
                          onClick={closeMobile}
                          data-testid={ch.testid}
                          className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-xs transition-colors ${isActive(ch.path) ? c.child : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/40"}`}
                        >
                          <ChIcon className="w-3.5 h-3.5" />
                          <span>{ch.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* User Card & Logout Footer */}
        <div className="p-3 border-t border-slate-800/80 bg-[#090f1d]/90">
          <div className="flex items-center justify-between p-2 rounded-lg bg-slate-900/60 border border-slate-800">
            <div
              className="flex items-center gap-2.5 min-w-0 cursor-pointer hover:bg-slate-800/60 rounded-lg p-1 -m-1 transition-colors"
              onClick={() => setShowProfile(true)}
              data-testid="user-profile-card"
              title="Klik untuk ubah foto profil, username & password"
            >
              <div className="w-8 h-8 rounded-full bg-cyan-600/30 border border-cyan-500/40 flex items-center justify-center text-cyan-300 font-bold text-xs uppercase overflow-hidden">
                {user?.photo ? <img src={user.photo} alt="Foto profil" className="w-full h-full object-cover" /> : (user?.name?.[0] || "U")}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-slate-200 truncate leading-tight">{user?.name || "User"}</p>
                <span className="inline-block text-[10px] uppercase font-bold px-1.5 py-0.2 rounded bg-cyan-500/20 text-cyan-300 mt-0.5">{role}</span>
              </div>
            </div>
            <button onClick={handleLogout} data-testid="logout-btn" title="Logout" className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-md transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>
      {showProfile && <ProfileModal onClose={() => setShowProfile(false)} />}
    </>
  );
};
